import React from "react";
import { Resource } from "../../types/resources";

type ExtendedResource = Resource & {
  hasActiveShift?: boolean;
  currentLocation?: {
    lat: number;
    lng: number;
    updatedAt?: Date;
  };
};

interface ResourceCardProps {
  resource: ExtendedResource;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (resource: ExtendedResource) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  className = "",
  style = {},
  onClick,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(resource);
    }
  };

  const isOnShift = (resource as any).hasActiveShift;
  const hasLocation = !!(resource as any).currentLocation?.lat;

  return (
    <div
      className={`resource-card ${className}`}
      style={{
        padding: "8px",
        borderRadius: "4px",
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        ...style,
      }}
      onClick={handleClick}
    >
      {/* Avatar with on-shift indicator */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            backgroundColor: "#E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: 600,
            color: "#374151",
            overflow: "hidden",
          }}
        >
          {(resource as any).avatar || (resource as any).profilePicture ? (
            <img
              src={(resource as any).avatar || (resource as any).profilePicture}
              alt={resource.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                // Fallback to initials if image fails
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            // Show initials
            resource.name
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)
          )}
        </div>
        {/* On-shift indicator - green dot */}
        {isOnShift && (
          <div
            style={{
              position: "absolute",
              bottom: "0",
              right: "0",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#10B981",
              border: "2px solid white",
              boxShadow: "0 0 0 1px rgba(16, 185, 129, 0.3)",
            }}
            title="On Shift"
          />
        )}
      </div>
      {/* Name and status */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="resource-title font-medium"
          style={{
            fontSize: "13px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {resource.name}
        </div>
        {isOnShift && (
          <div
            style={{
              fontSize: "11px",
              color: "#10B981",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span>On Shift</span>
            {hasLocation && (
              <span style={{ color: "#6B7280" }}>• GPS Active</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceCard;
